;(function () {
	// Wayward product gallery.
	// Desktop: the gallery is one sticky frame (big image + tile panel). Scrolling steps through every
	//          picture, the last one included; click a tile to jump to it, click the big image to zoom.
	// Mobile:  CSS swipe carousel with dots.
	// Layout itself is pure CSS (section-main-product-wayward.css).
	const MOBILE = window.matchMedia('(max-width: 749px)')
	const DESKTOP = window.matchMedia('(min-width: 990px)')
	let lightbox

	// ---------- Lightbox ----------
	const buildLightbox = () => {
		if (lightbox) return lightbox
		const el = document.createElement('div')
		el.className = 'wg-lightbox'
		el.setAttribute('role', 'dialog')
		el.setAttribute('aria-modal', 'true')
		el.setAttribute('aria-label', 'Product images')
		el.hidden = true
		el.innerHTML =
			"<button type='button' class='wg-lightbox__close' aria-label='Close'>&times;</button>" +
			"<button type='button' class='wg-lightbox__nav wg-lightbox__nav--prev' aria-label='Previous image'>&#8249;</button>" +
			"<img class='wg-lightbox__img' alt=''>" +
			"<button type='button' class='wg-lightbox__nav wg-lightbox__nav--next' aria-label='Next image'>&#8250;</button>" +
			"<span class='wg-lightbox__count' aria-live='polite'></span>"
		document.body.appendChild(el)

		const state = {el, img: el.querySelector('.wg-lightbox__img'), count: el.querySelector('.wg-lightbox__count'), items: [], i: 0, opener: null}
		const show = (i) => {
			state.i = (i + state.items.length) % state.items.length
			const item = state.items[state.i]
			state.img.src = item.full
			state.img.alt = item.alt || ''
			state.count.textContent = `${state.i + 1} / ${state.items.length}`
		}
		state.open = (items, i, opener) => {
			state.items = items
			state.opener = opener
			show(i)
			el.hidden = false
			document.documentElement.classList.add('wg-lightbox-open')
			el.querySelector('.wg-lightbox__close').focus()
		}
		state.close = () => {
			el.hidden = true
			state.img.removeAttribute('src')
			document.documentElement.classList.remove('wg-lightbox-open')
			if (state.opener) state.opener.focus()
		}
		el.querySelector('.wg-lightbox__close').addEventListener('click', state.close)
		el.querySelector('.wg-lightbox__nav--prev').addEventListener('click', () => show(state.i - 1))
		el.querySelector('.wg-lightbox__nav--next').addEventListener('click', () => show(state.i + 1))
		el.addEventListener('click', (e) => {
			if (e.target === el) state.close()
		})
		document.addEventListener('keydown', (e) => {
			if (el.hidden) return
			if (e.key === 'Escape') state.close()
			if (e.key === 'ArrowLeft') show(state.i - 1)
			if (e.key === 'ArrowRight') show(state.i + 1)
		})
		lightbox = state
		return state
	}

	// ---------- One gallery ----------
	const initGallery = (gallery) => {
		if (!gallery || gallery.dataset.wgReady) return
		gallery.dataset.wgReady = 'true'

		const heroWrap = gallery.querySelector('.wg-gallery__hero .wg-media')
		const heroOpener = heroWrap && heroWrap.querySelector('[data-wg-open]')
		const heroImg = heroOpener && heroOpener.querySelector('img')
		const tileEls = Array.from(gallery.querySelectorAll('.wg-gallery__rest .wg-media'))

		// Every picture in order. Entry 0 is the original big image; the rest are the tiles.
		const entries = [heroWrap, ...tileEls]
			.map((el) => {
				const opener = el && el.querySelector('[data-wg-open]')
				if (!opener) return null
				const img = opener.querySelector('img')
				return {el, opener, srcset: img.getAttribute('srcset') || '', src: img.currentSrc || img.src, alt: img.alt || '', full: opener.dataset.full}
			})
			.filter(Boolean)
		const canSwap = !!(heroImg && entries.length > 1 && entries[0].el === heroWrap)
		const wrap = gallery.parentElement
		const panel = gallery.querySelector('.wg-gallery__panel')
		const rest = gallery.querySelector('.wg-gallery__rest')
		if (wrap) wrap.style.setProperty('--wg-count', entries.length)
		let current = 0

		const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
		let swapToken = 0
		// Changing picture: the new one swipes up into the frame while the old one is carried out of the top
		// (reversed when scrolling back up). The new picture is fetched first so it never pops in half loaded.
		const setHero = (i) => {
			if (!canSwap || i === current || !entries[i]) return
			const dir = i > current ? 1 : -1
			current = i
			entries.forEach((en) => en.opener.removeAttribute('aria-current'))
			if (i > 0) entries[i].opener.setAttribute('aria-current', 'true')
			const e = entries[i]
			const mine = ++swapToken
			const apply = () => {
				if (mine !== swapToken) return
				heroOpener.querySelectorAll('.wg-media__img--ghost').forEach((g) => g.remove())
				heroImg.getAnimations().forEach((a) => a.cancel())
				const canAnimate = typeof heroImg.animate === 'function' && !reduceMotion.matches
				let ghost
				if (canAnimate) {
					ghost = heroImg.cloneNode()
					ghost.classList.add('wg-media__img--ghost')
					ghost.setAttribute('aria-hidden', 'true')
					heroOpener.appendChild(ghost)
				}
				heroImg.srcset = e.srcset
				heroImg.src = e.src
				heroImg.alt = e.alt
				heroOpener.dataset.full = e.full
				if (!ghost) return
				const timing = {duration: 700, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'both'}
				heroImg.animate([{transform: `translateY(${dir * 100}%)`}, {transform: 'translateY(0)'}], timing)
				ghost.animate([{transform: 'translateY(0)'}, {transform: `translateY(${dir * -100}%)`}], timing)
			window.setTimeout(() => ghost.remove(), timing.duration + 60)
			}
			const pre = new Image()
			pre.onload = pre.onerror = apply
			if (heroImg.sizes) pre.sizes = heroImg.sizes
			if (e.srcset) pre.srcset = e.srcset
			pre.src = e.src
			if (pre.complete) apply()
		}

		// Progress (0 to 1) through the sticky frame: 0 at the top of the gallery, 1 when the last picture is showing.
		const stickyTop = () => parseFloat(getComputedStyle(gallery.closest('.wg-main')).getPropertyValue('--wg-sticky-top')) || 88
		const geometry = () => {
			const line = stickyTop()
			const box = wrap.getBoundingClientRect()
			const total = Math.max(0, wrap.offsetHeight - gallery.offsetHeight)
			return {line, total, docTop: box.top + window.scrollY, scrolled: Math.min(Math.max(line - box.top, 0), total)}
		}
		const follow = () => {
			if (!DESKTOP.matches || !canSwap || !wrap) return
			const g = geometry()
			const p = g.total > 0 ? g.scrolled / g.total : 0
			const maxShift = panel && rest ? Math.max(0, rest.scrollHeight - panel.clientHeight) : 0
			if (rest) rest.style.setProperty('--wg-panel-y', `${(-p * maxShift).toFixed(1)}px`)
			setHero(Math.round(p * (entries.length - 1)))
		}
		let ticking = false
		window.addEventListener(
			'scroll',
			() => {
				if (ticking) return
				ticking = true
				window.requestAnimationFrame(() => {
					ticking = false
					follow()
				})
			},
			{passive: true}
		)
		window.addEventListener('wg:refit', follow)
		DESKTOP.addEventListener('change', () => {
			if (rest) rest.style.removeProperty('--wg-panel-y')
			setHero(0)
			follow()
		})
		follow()

		gallery.addEventListener('click', (e) => {
			const opener = e.target.closest('[data-wg-open]')
			if (!opener) return
			const inTiles = !!opener.closest('.wg-gallery__rest')
			const index = entries.findIndex((en) => en.opener === opener)
			if (inTiles && DESKTOP.matches && canSwap && wrap) {
				// desktop: jump to that picture's place in the scroll. Everything you can see is sticky,
				// so this looks like the big image simply changing.
				const g = geometry()
				const top = g.docTop - g.line + (index / Math.max(1, entries.length - 1)) * g.total
				if (window.waywardLenis) window.waywardLenis.scrollTo(top, {immediate: true})
				else window.scrollTo({top, behavior: 'instant'})
				setHero(index)
				follow()
				return
			}
			const items = entries.map((en) => ({full: en.full, alt: en.alt}))
			buildLightbox().open(items, inTiles ? index : current, opener)
		})

		// ---------- Mobile: swipe carousel with dots ----------
		const dots = gallery.parentElement.querySelector('.wg-gallery__dots')
		const tiles = () => Array.from(gallery.querySelectorAll('.wg-media'))
		const scrollToTile = (i) => {
			const tile = tiles()[i]
			if (tile && MOBILE.matches) gallery.scrollTo({left: tile.offsetLeft, behavior: 'smooth'})
		}
		const updateDots = () => {
			if (!dots) return
			const i = Math.round(gallery.scrollLeft / Math.max(gallery.clientWidth, 1))
			Array.from(dots.children).forEach((d, n) => d.setAttribute('aria-current', n === i ? 'true' : 'false'))
		}
		if (dots) {
			dots.innerHTML = ''
			tiles().forEach((_, i) => {
				const dot = document.createElement('button')
				dot.type = 'button'
				dot.setAttribute('aria-label', `Go to image ${i + 1}`)
				dot.addEventListener('click', () => scrollToTile(i))
				dots.appendChild(dot)
			})
			updateDots()
		}
		gallery.addEventListener('scroll', () => window.requestAnimationFrame(updateDots), {passive: true})

		// A variant with its own image: jump to it (mobile carousel only)
		document.addEventListener('variant:change', (e) => {
			const media = e.detail && e.detail.variant && e.detail.variant.featured_media
			if (!media || !MOBILE.matches) return
			const tile = gallery.querySelector(`.wg-media[data-media-id$="-${media.id}"]`)
			if (tile) gallery.scrollTo({left: tile.offsetLeft, behavior: 'smooth'})
		})
	}

	// "Size guide" links reuse the modal that theme.liquid already provides
	document.addEventListener('click', (e) => {
		const link = e.target.closest('[data-open-size-guide]')
		if (!link || typeof window.openSizeGuide !== 'function') return
		e.preventDefault()
		window.openSizeGuide()
	})

	// ---------- Desktop first screen ----------
	// Size the big image to what is left below the announcement bar and header (measured, because the
	// theme's header-height variable is not reliable on load), and pin the sticky columns under the header.
	const fitFold = () => {
		document.querySelectorAll('.wg-main').forEach((main) => {
			const wrap = main.querySelector('.wg-gallery-wrap')
			if (!wrap) return
			const header = document.querySelector('.shopify-section-header')
			const headerHeight = header ? header.getBoundingClientRect().height : 0
			// Where the gallery starts on the page with the announcement bar showing. Use the bar's full height
			// (not its current, possibly collapsed one) so the frame never changes size when the bar slides away.
			const bar = document.querySelector('.section-announcement')
			const barFull = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--announcement-height')) || 0
			const barNow = bar ? bar.getBoundingClientRect().height : 0
			const top = wrap.getBoundingClientRect().top + window.scrollY + Math.max(0, barFull - barNow)
			let fold = Math.min(960, Math.max(440, window.innerHeight - top - 24))
			if (!main.classList.contains('wg-main--fold')) {
				const hero = main.querySelector('.wg-gallery__hero')
				if (hero) fold = Math.round(hero.getBoundingClientRect().width)
			}
			main.style.setProperty('--wg-fold-height', `${Math.round(fold)}px`)
			main.style.setProperty('--wg-sticky-top', `${Math.round(headerHeight + 20)}px`)
			// Spread the buy box out to fill the height of the picture frame, never past its bottom edge:
			// pick the roomiest vertical spacing (--wg-v) at which the whole box still fits.
			const info = main.querySelector('.wg-info')
			if (info && window.matchMedia('(min-width: 990px)').matches) {
				let lo = 0.3
				// socks, ball markers and towels have a short buy box: don't stretch it to fill the picture, keep it compact
				const compact = /template-product-(socks|ball-markers|towels)/.test(document.body.className)
				let hi = compact ? 1.2 : 1.7
				for (let n = 0; n < 8; n++) {
					const mid = (lo + hi) / 2
					main.style.setProperty('--wg-v', mid.toFixed(3))
					if (info.offsetHeight <= Math.round(fold)) lo = mid
					else hi = mid
				}
				main.style.setProperty('--wg-v', lo.toFixed(3))
			} else {
				main.style.removeProperty('--wg-v')
			}
		})
		window.dispatchEvent(new Event('wg:refit'))
	}
	let resizeTimer
	window.addEventListener('resize', () => {
		clearTimeout(resizeTimer)
		resizeTimer = setTimeout(fitFold, 120)
	})
	window.addEventListener('load', fitFold)
	// The header finishes sizing after this script runs: re-measure when it does
	if ('ResizeObserver' in window) {
		const watched = new ResizeObserver(() => fitFold())
		document.querySelectorAll('.shopify-section-header').forEach((el) => watched.observe(el))
	}
	if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitFold)

	const initAll = (root) => {
		;(root || document).querySelectorAll('[data-wg-gallery]').forEach(initGallery)
		fitFold()
	}
	initAll()
	document.addEventListener('shopify:section:load', (e) => initAll(e.target))
})()
